/*
 * KARAAT — Carat size on a hand (2026-10-16). Drawer markup + config: sections/carat-on-hand.liquid.
 *
 * 1. Injects a "See carat size on a hand" link under the VO Product Options diamond-size
 *    selector (div.best-custom-product-options). VO rebuilds its DOM on every option change,
 *    so injection re-runs from a debounced MutationObserver and is guarded against duplicates.
 *    LOCALE-SAFE: the option is found by its values ("0.25ct", "1.00ct", …), never its name.
 * 2. On click, reads the selected carat (VO's checked value → product form variant → first
 *    value) and opens the theme drawer at that size.
 * 3. Draws the stone at real mm size on the hand photo:
 *      image px per mm = finger width in the photo (px) / finger width (mm)
 *    where finger width (mm) = ring size (Nordic size = inner diameter, mm) × FINGER_WIDTH_PER_RING_MM.
 *    Stone face sizes scale with the cube root of carat weight from 1 ct reference sizes.
 * Fails silently: VO markup is third-party; nothing here may break the product page.
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-coh]');
  if (!root || root.__cohInit) { return; }
  root.__cohInit = true;

  var cfg;
  try { cfg = JSON.parse(root.querySelector('[data-coh-config]').textContent); } catch (e) { return; }
  if (!cfg.carats || !cfg.carats.length) { return; }
  // Extend the slider past the product's largest size (e.g. 3 ct) in 0.5 ct steps up to maxCarat.
  (function () {
    var max = +cfg.maxCarat || 0;
    for (var c = cfg.carats[cfg.carats.length - 1] + 0.5; c <= max + 1e-9; c += 0.5) {
      cfg.carats.push(Math.round(c * 100) / 100);
    }
  })();

  // Face-up size of a 1 ct stone, [length, width] in mm (length runs along the finger), from
  // standard diamond size charts. Each stone photo is fitted to these measurements, so a shape
  // always shows a typical cut's proportions even if the photo is a little longer or wider.
  var BASE = {
    round: [6.5, 6.5],
    oval: [7.7, 5.7],
    pear: [8.5, 5.5],
    emerald: [7.0, 4.9],
    cushion: [5.6, 5.4],
    radiant: [6.6, 4.8],
    marquise: [10.0, 5.0]
  };
  // Band metals. Each has its own hand photo (same photo, only the band differs) and a swatch
  // colour for its button. Matched against the product's chosen Metal in EN / FI / SV; rose and
  // white are tested before yellow because "roseguld" and "vitguld" also contain "gul".
  var METALS = ['yellow', 'white', 'rose'];
  var METAL_DOT = { yellow: '#c9ad6a', white: '#dcdcdc', rose: '#d1a58f' };
  var METAL_RE = [
    ['rose', /ros[eé]|ruusu/i], // Swedish "roséguld" has an é
    ['white', /white|valko|vit|platin/i],
    ['yellow', /yellow|kelta|gul/i]
  ];
  var SHAPES = Object.keys(BASE).filter(function (s) { return cfg.stones && cfg.stones[s]; });
  // Seen from above, a finger at its base is a flattened oval: wider than the ring's inner
  // diameter (the ring size). 1.06 ≈ a 16.5 ring sits on a ~17.5 mm wide finger.
  var FINGER_WIDTH_PER_RING_MM = 1.06;
  var CARD_STEP = 0.02; // px per mm per − / + press
  var CARD_W_MM = 53.98; // upright bank card width
  var PXMM_KEY = 'karaat_coh_pxmm';
  var VO_SELECTOR = 'div.best-custom-product-options';
  // VO concatenates option texts ("0.25ct0.50ct…"), so no \b after "ct" (it's followed by a digit).
  var CT_RE = /\d+(?:[.,]\d+)?\s*ct(?![a-z])/gi;

  var lang = (document.documentElement.lang || 'en').toLowerCase();
  var state = {
    index: 0,
    shape: BASE[cfg.shape] ? cfg.shape : 'round',
    metal: 'yellow',
    actual: false,
    pxmm: 96 / 25.4,
    calibrated: false
  };
  try {
    var saved = parseFloat(localStorage.getItem(PXMM_KEY));
    if (saved > 2 && saved < 8) { state.pxmm = saved; state.calibrated = true; }
  } catch (e) {}

  var $ = function (sel) { return root.querySelector(sel); };
  var els = {
    open: $('[data-coh-open]'),
    view: $('[data-coh-view]'),
    hand: $('[data-coh-hand]'),
    stone: $('[data-coh-stone]'),
    readout: $('[data-coh-readout]'),
    carat: $('[data-coh-carat]'),
    fill: $('[data-coh-fill]'),
    dots: $('[data-coh-dots]'),
    min: $('[data-coh-min]'),
    max: $('[data-coh-max]'),
    cardWrap: $('[data-coh-card-wrap]'),
    shapes: $('[data-coh-shapes]'),
    metals: $('[data-coh-metals]'),
    actual: $('[data-coh-actual]'),
    calibrate: $('[data-coh-calibrate]'),
    card: $('[data-coh-card]'),
    cardRange: $('[data-coh-card-range]'),
    calDone: $('[data-coh-cal-done]'),
    recal: $('[data-coh-recal]'),
    stage: $('[data-coh-stage]')
  };

  function fmt(n, digits) {
    try { return Number(n).toLocaleString(lang, { maximumFractionDigits: digits }); }
    catch (e) { return String(Math.round(n * 100) / 100); }
  }

  /* ---------------- controls ---------------- */
  function buildControls() {
    els.carat.max = String(cfg.carats.length - 1);
    // Evenly spaced stop dots on the track, labels only at the two ends (the chosen size is in
    // the readout above), so the slider stays symmetrical however many sizes there are.
    var last = cfg.carats.length - 1 || 1;
    els.dots.innerHTML = cfg.carats.map(function (c, i) {
      return '<span class="coh-slider__dot" style="left: ' + (i / last * 100) + '%"></span>';
    }).join('');
    els.min.textContent = fmt(cfg.carats[0], 2) + ' ct';
    els.max.textContent = fmt(cfg.carats[last], 2) + ' ct';
    els.carat.addEventListener('input', function () { state.index = +els.carat.value; render(); });

    els.shapes.innerHTML = SHAPES.map(function (s) {
      return '<button type="button" class="coh-chip" data-shape="' + s + '">'
        + '<img src="' + cfg.stones[s] + '" alt="" aria-hidden="true" loading="lazy">'
        + '<span>' + ((cfg.shapeNames && cfg.shapeNames[s]) || s) + '</span></button>';
    }).join('');
    els.shapes.addEventListener('click', function (e) {
      var b = e.target.closest('[data-shape]');
      if (b) { state.shape = b.getAttribute('data-shape'); render(); }
    });

    els.metals.innerHTML = METALS.filter(function (m) { return cfg.hands && cfg.hands[m]; }).map(function (m) {
      return '<button type="button" class="coh-chip coh-chip--metal" data-metal="' + m + '">'
        + '<span class="coh-metal-dot" style="background:' + METAL_DOT[m] + '" aria-hidden="true"></span>'
        + '<span>' + ((cfg.metalNames && cfg.metalNames[m]) || m) + '</span></button>';
    }).join('');
    els.metals.addEventListener('click', function (e) {
      var b = e.target.closest('[data-metal]');
      if (b) { state.metal = b.getAttribute('data-metal'); render(); }
    });


    // Actual size: screens differ, so the shopper first matches a card outline to a real bank
    // card once (remembered in localStorage), then the photo is drawn at true physical size.
    els.actual.addEventListener('change', function () {
      state.actual = els.actual.checked;
      showCalibration(state.actual && !state.calibrated);
      render();
    });
    els.cardRange.value = String(state.pxmm);
    els.cardRange.addEventListener('input', function () {
      state.pxmm = parseFloat(els.cardRange.value);
      render();
    });
    root.addEventListener('click', function (e) {
      var b = e.target.closest('[data-coh-card-step]');
      if (!b) { return; }
      state.pxmm = Math.min(maxPxmm(), Math.max(2.5, state.pxmm + CARD_STEP * +b.getAttribute('data-coh-card-step')));
      els.cardRange.value = String(state.pxmm);
      render();
    });
    els.calDone.addEventListener('click', function () {
      state.calibrated = true;
      try { localStorage.setItem(PXMM_KEY, String(state.pxmm)); } catch (e) {}
      showCalibration(false);
      render();
      try { els.stage.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch (e) {}
    });
    els.recal.addEventListener('click', function () { showCalibration(true); });
    window.addEventListener('resize', function () { if (state.actual) { render(); } });
  }

  // Largest scale at which the upright card (53.98 mm wide) still fits the panel, so the card
  // can never be cut off; the calibration slider stops there.
  function maxPxmm() {
    var w = (els.cardWrap && els.cardWrap.clientWidth) || 0;
    return w > 0 ? Math.min(8, (w - 2) / CARD_W_MM) : 8;
  }
  function showCalibration(on) {
    els.calibrate.hidden = !on;
    els.recal.hidden = on || !state.actual;
    if (on) {
      var max = maxPxmm();
      els.cardRange.max = String(max);
      if (state.pxmm > max) { state.pxmm = max; }
      els.cardRange.value = String(state.pxmm);
      render();
    }
  }

  function setPressed(container, attr, value) {
    var btns = container.querySelectorAll('[' + attr + ']');
    for (var i = 0; i < btns.length; i++) {
      btns[i].setAttribute('aria-pressed', String(btns[i].getAttribute(attr) === String(value)));
    }
  }

  /* ---------------- drawing ---------------- */
  function render() {
    var h = cfg.hand;
    var carat = cfg.carats[state.index];
    var base = BASE[state.shape] || BASE.round;
    // Weight grows with volume (size³), so face-up size grows with the cube root of carat weight.
    // This matches size charts to ±0.1 mm (round: 0.25 ct 4.1 mm, 1 ct 6.5, 2 ct 8.2, 5 ct 11.1).
    var k = Math.cbrt(carat);
    var lenMm = base[0] * k;
    var widMm = base[1] * k;
    // Image px per mm, from the photographed finger and its ring size.
    var imgPxMm = (h.fingerWidth * h.width) / (h.ringSize * FINGER_WIDTH_PER_RING_MM);

    // Visible region of the photo, in image px, around the ring.
    var viewW = h.zoom * h.width;
    var aspect = 1.1; // view height / width
    var stage = els.view.parentNode;
    var cssW = stage.clientWidth || 360;
    if (state.actual) {
      // At actual size 1 image px = pxmm / imgPxMm CSS px; shrink the region to fit the panel.
      var scale = state.pxmm / imgPxMm;
      viewW = Math.min(viewW, cssW / scale);
      cssW = viewW * scale;
    }
    var viewH = viewW * aspect;
    var cx = h.x * h.width;
    var cy = h.y * h.height;
    var x0 = Math.max(0, Math.min(h.width - viewW, cx - viewW / 2));
    var y0 = Math.max(0, Math.min(h.height - viewH, cy - viewH * 0.6)); // more finger above the ring

    els.view.style.width = state.actual ? cssW + 'px' : '';
    els.view.style.aspectRatio = viewW + ' / ' + viewH;
    els.hand.style.width = (h.width / viewW * 100) + '%';
    els.hand.style.left = (-x0 / viewW * 100) + '%';
    els.hand.style.top = (-y0 / viewH * 100) + '%';

    // Display scale (section setting "Diamond display size") tunes how big stones look on the
    // photo; the mm readout below always shows the true size-chart measurements.
    var vis = cfg.stoneScale > 0 ? cfg.stoneScale : 1;
    var sw = widMm * imgPxMm * vis;
    var sh = lenMm * imgPxMm * vis;
    if (els.stone.getAttribute('src') !== cfg.stones[state.shape]) { els.stone.src = cfg.stones[state.shape]; }
    els.stone.style.width = (sw / viewW * 100) + '%';
    els.stone.style.height = (sh / viewH * 100) + '%';
    els.stone.style.left = ((cx - x0 - sw / 2) / viewW * 100) + '%';
    els.stone.style.top = ((cy - y0 - sh / 2) / viewH * 100) + '%';

    var mm = state.shape === 'round'
      ? fmt(widMm, 1) + ' mm'
      : fmt(lenMm, 1) + ' × ' + fmt(widMm, 1) + ' mm';
    var name = (cfg.shapeNames && cfg.shapeNames[state.shape]) || state.shape;
    els.readout.textContent = (cfg.readout || '[carat] ct [shape] · [mm]')
      .replace('[carat]', fmt(carat, 2)).replace('[shape]', name).replace('[mm]', mm);

    els.carat.value = String(state.index);
    var lastStop = cfg.carats.length - 1 || 1;
    els.fill.style.width = (state.index / lastStop * 100) + '%';
    var dots = els.dots.children;
    for (var d = 0; d < dots.length; d++) { dots[d].classList.toggle('is-on', d <= state.index); }
    els.carat.setAttribute('aria-valuetext', fmt(carat, 2) + ' ct');
    setPressed(els.shapes, 'data-shape', state.shape);
    setPressed(els.metals, 'data-metal', state.metal);
    var handSrc = handFor(state.metal);
    if (els.hand.getAttribute('src') !== handSrc) { els.hand.src = handSrc; }

    els.card.style.setProperty('--coh-mm', state.pxmm + 'px'); // card is drawn in mm (see CSS)
    els.recal.hidden = !state.actual || !els.calibrate.hidden;
  }

  /* ---------------- selected carat ---------------- */
  function parseCt(text) {
    var m = String(text || '').match(/(\d+(?:[.,]\d+)?)\s*ct/i);
    return m ? parseFloat(m[1].replace(',', '.')) : null;
  }
  function nearestIndex(carat) {
    var best = 0;
    for (var i = 0; i < cfg.carats.length; i++) {
      if (Math.abs(cfg.carats[i] - carat) < Math.abs(cfg.carats[best] - carat)) { best = i; }
    }
    return best;
  }
  function selectedCarat(group) {
    try {
      if (group) {
        // VO renders the size option as a <select> (the group itself) with the chosen value
        // echoed in the wrapper's .bcpo-value label.
        if (group.tagName === 'SELECT' && parseCt(group.value) != null) { return parseCt(group.value); }
        var wrap = group.closest('.selector-wrapper');
        var shown = wrap && wrap.querySelector('.bcpo-value');
        if (shown && parseCt(shown.textContent) != null) { return parseCt(shown.textContent); }
        var checked = group.querySelector('input:checked');
        if (checked) {
          var c = parseCt(checked.value);
          if (c == null) {
            var lbl = (checked.id && group.querySelector('label[for="' + checked.id + '"]')) || checked.closest('label');
            c = lbl ? parseCt(lbl.textContent) : null;
          }
          if (c != null) { return c; }
        }
        var sel = group.querySelector('select');
        if (sel && parseCt(sel.value) != null) { return parseCt(sel.value); }
        var on = group.querySelector('[aria-checked="true"], [aria-selected="true"], .selected, .is-selected, .active');
        if (on && parseCt(on.textContent) != null) { return parseCt(on.textContent); }
      }
      var idInput = document.querySelector('form[action*="/cart/add"] [name="id"]');
      var vid = (idInput && idInput.value) || new URLSearchParams(location.search).get('variant');
      if (vid && cfg.variants && cfg.variants[vid] != null) { return cfg.variants[vid]; }
    } catch (e) {}
    return null;
  }

  /* ---------------- open ---------------- */
  var built = false;
  // The hand photo (~200 KB) loads on first intent (hover/touch/focus of the link), not with the page.
  function handFor(metal) {
    return (cfg.hands && cfg.hands[metal]) || els.hand.getAttribute('data-src');
  }
  function preload() {
    if (!els.hand.getAttribute('src')) { els.hand.src = handFor(selectedMetal() || state.metal); }
  }
  // The product's chosen metal, read from VO's option labels ("Metal: 18K Yellow gold").
  function selectedMetal() {
    try {
      var vo = document.querySelector(VO_SELECTOR);
      var vals = vo ? vo.querySelectorAll('.bcpo-value, select') : [];
      for (var i = 0; i < vals.length; i++) {
        var t = vals[i].tagName === 'SELECT' ? vals[i].value : vals[i].textContent;
        for (var j = 0; j < METAL_RE.length; j++) {
          if (METAL_RE[j][1].test(t || '')) { return METAL_RE[j][0]; }
        }
      }
    } catch (e) {}
    return null;
  }
  function openDrawer(group) {
    if (!built) { buildControls(); built = true; }
    state.metal = selectedMetal() || state.metal;
    preload();
    var c = selectedCarat(group);
    state.index = nearestIndex(c != null ? c : 1);
    render();
    els.open.click();
    setTimeout(render, 60); // re-measure once the drawer has laid out
    track(c);
  }

  /* ---------------- VO trigger injection ---------------- */
  function findSizeGroup(vo) {
    // Smallest element whose text holds 3+ distinct carat values = the diamond-size option.
    var all = vo.querySelectorAll('*');
    var best = null;
    for (var i = 0; i < all.length; i++) {
      var m = (all[i].textContent || '').match(CT_RE);
      if (!m) { continue; }
      var uniq = {};
      m.forEach(function (x) { uniq[x.replace(/\s/g, '').toLowerCase()] = 1; });
      if (Object.keys(uniq).length >= 3 && (!best || best.contains(all[i]))) { best = all[i]; }
    }
    return best;
  }
  function inject() {
    try {
      var vo = document.querySelector(VO_SELECTOR);
      if (!vo || vo.querySelector('.coh-trigger')) { return; }
      var group = findSizeGroup(vo);
      if (!group) { return; }
      // Insert BELOW the whole size option: after VO's .selector-wrapper (label + dropdown).
      // Inside the wrapper the link sat beside the dropdown, because the wrapper lays out in a row.
      var anchor = group.closest('.selector-wrapper');
      if (!anchor || !vo.contains(anchor)) {
        anchor = group;
        while (anchor.parentNode && anchor.parentNode !== vo && anchor.parentNode.children.length === 1) { anchor = anchor.parentNode; }
      }
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'coh-trigger';
      btn.textContent = cfg.triggerLabel || 'See carat size on a hand';
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        openDrawer(findSizeGroup(document.querySelector(VO_SELECTOR) || vo));
      });
      btn.addEventListener('pointerenter', preload);
      btn.addEventListener('touchstart', preload, { passive: true });
      btn.addEventListener('focus', preload);
      anchor.parentNode.insertBefore(btn, anchor.nextSibling);
    } catch (e) { /* fail silently */ }
  }
  var scheduled = false;
  function schedule() {
    if (scheduled) { return; }
    scheduled = true;
    setTimeout(function () { scheduled = false; inject(); }, 16); // setTimeout, not rAF (paused in background tabs)
  }
  inject();
  try { new MutationObserver(schedule).observe(document.body, { childList: true, subtree: true }); } catch (e) {}

  var editorBtn = root.querySelector('[data-coh-editor-open]');
  if (editorBtn) { editorBtn.addEventListener('click', function () { openDrawer(null); }); }

  /* ---------------- GA4 (consent-gated, same as the diamond-quality drawer) ---------------- */
  var GA4_ID = 'G-7KMHFELSCH';
  window.dataLayer = window.dataLayer || [];
  function gtag() { window.dataLayer.push(arguments); }
  if (!window.__karaatGa4Configured) {
    try { gtag('config', GA4_ID, { send_page_view: false }); } catch (e) {}
    window.__karaatGa4Configured = true;
  }
  function consentGranted() {
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      if (cp && typeof cp.analyticsProcessingAllowed === 'function') { return cp.analyticsProcessingAllowed(); }
    } catch (e) {}
    return false;
  }
  var pending = null;
  function send(c) {
    var params = { product_handle: cfg.handle, carat: c == null ? '(unknown)' : String(c) };
    try { gtag('event', 'carat_on_hand_opened', params); } catch (e) {}
  }
  function track(c) {
    if (consentGranted()) { send(c); } else { pending = { c: c }; }
  }
  document.addEventListener('visitorConsentCollected', function () {
    if (pending && consentGranted()) { send(pending.c); pending = null; }
  });
})();
